import { useCallback, type FC, type ReactNode } from 'react';

import MenuIcon from '@mui/icons-material/Menu';
import { Trans, useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { MenuButton } from '../../../../../components/base';
import { DownloadExcelTemplate } from '../../../../../components/ControlPanel/components/DownloadExcelTemplate';
import { ImportExcelButton } from '../../../../../components/ControlPanel/components/ImportExcelButton';
import { FullScreenButton } from '../../../../../components/FullScreen';
import useDrawer from '../../../../../hooks/useDrawer';
import { AddEntityControl, ButtonGridColumns, ControlPanelWrapper } from '../../../../../modules/EntitiesGrid';
import { TaskFilters } from '../../../../../modules/Tasks/components/TaskFilters';
import { newEntityReady, projectTasksFiltersState } from '../../../../../modules/Tasks/state/projectTasks';
import { useProjectTaskNewRow } from '../../../../../modules/Tasks/state/projectTasks/hooks';
import { Drawer } from '../../../../../state/drawer';
import { permissionState } from '../../../../../state/event';
import { useExportToPdfTableProjectTasks } from '../../../../../state/export';
import { userEventSettings } from '../../../../../state/user';
import { useToggleCompactMode } from '../../../../event-settings';

type Props = {
  onExportToExcel: () => void;
  onImportFromExcel: (file: File) => void;
  loadingImportFromExcel?: boolean;
  fontSizeControl?: ReactNode;
};

export const ControlPanel: FC<Props> = ({
  onExportToExcel,
  onImportFromExcel,
  loadingImportFromExcel,
  fontSizeControl,
}) => {
  const { t } = useTranslation();
  const { showDrawer } = useDrawer();
  const {
    loading: loadingNewRow,
    newEntity,
    prepareNewEntity,
    destroyNewEntity,
    addNewEntity,
  } = useProjectTaskNewRow();
  const { canCreateProjectTask } = useRecoilValue(permissionState);
  const { loading: loadingExportPdfTable, execute: exportToPdfTable } = useExportToPdfTableProjectTasks();
  const userEventSetting = useRecoilValue(userEventSettings);
  const { execute: toggleCompactMode, loading: loadingToggleCompactMode } = useToggleCompactMode('pmp');

  const exportToPdfTableCallback = useCallback(() => exportToPdfTable(t), [exportToPdfTable, t]);
  const loading = loadingImportFromExcel || loadingExportPdfTable || loadingToggleCompactMode;

  return (
    <ControlPanelWrapper
      leftSlot={
        <AddEntityControl
          disabled={!canCreateProjectTask}
          loading={loadingNewRow}
          newEntity={newEntity}
          destroyNewEntity={destroyNewEntity}
          addNewEntity={addNewEntity}
          newEntityReady={newEntityReady}
          addNewButtons={[
            {
              text: t('add-new-project-task-inline'),
              action: prepareNewEntity,
            },
          ]}
        />
      }
      rightSlot={
        <>
          <TaskFilters
            filtersState={projectTasksFiltersState}
            onClickFilterButton={() => showDrawer({ type: Drawer.PROJECT_TASKS_FILTER })}
          />
          <ButtonGridColumns
            openGridColumnsSettings={() => showDrawer({ type: Drawer.PROJECT_TASKS_COLUMNS })}
            columnsText={t('columns')}
          />
          {fontSizeControl}
          <FullScreenButton />
          <MenuButton
            buttonContent={<MenuIcon />}
            isLoading={loading}
            commands={[
              {
                content: (
                  <Trans
                    i18nKey={
                      userEventSetting.pmpGridSettings.viewMode === 'compact'
                        ? 'table-compact-disable'
                        : 'table-compact-enable'
                    }
                  />
                ),
                onClick: toggleCompactMode,
              },
              ...(canCreateProjectTask
                ? [
                    {
                      content: <ImportExcelButton onClick={onImportFromExcel} />,
                      disableAutoClose: true,
                    },
                  ]
                : []),
              ...(canCreateProjectTask
                ? [
                    {
                      content: <DownloadExcelTemplate link='/excelTemplates/projectTasksTemplate.xlsx' />,
                    },
                  ]
                : []),
              ...(onExportToExcel
                ? [
                    {
                      content: <Trans i18nKey='export-to-excel' />,
                      onClick: onExportToExcel,
                    },
                  ]
                : []),
              {
                content: <Trans i18nKey='export-table' />,
                onClick: exportToPdfTableCallback,
              },
            ]}
          />
        </>
      }
    />
  );
};
